import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  PersonIcon,
  OrganizationIcon,
  ProductIcon,
  PageAttachmentIcon,
  ProductReferenceIcon,
  ListNumberedIcon,
  SettingsIcon,
  InfoIcon,
  PersonLockIcon,
  InventoryIcon,
  OrderIcon,
  DeliveryIcon,
  AirplaneIcon,
  MegaphoneIcon,
  CalendarIcon,
  ThumbsUpIcon,
  EmailIcon,
  ReceiptIcon,
  ClipboardCheckIcon,
  CalendarTimeIcon,
  FileIcon,
  ShieldCheckMarkIcon,
  ProfileIcon,
} from "@shopify/polaris-icons";
import { settingsAreaPaths } from "containers/settings/settings-sections";
import {
  PERSONALAS_MENU_GROUPS,
  personalasHomePath,
} from "features/personnel/personnel-helpers";
import { selectCabinetSummary } from "features/cabinet-tasks/cabinet-summary.slice";

// How the sidebar reads this list:
// - Items appear in array order, and the layout draws a group title above the
//   first visible item of each group (cabinet, then company, then modules).
// - An item is visible when the viewer holds one of its isVisibleOnlyFor
//   permission groups (no list means everyone), unless it is marked
//   notForGuestMember and the viewer is a guest member.
// - Items marked isVisibleToGroupVisitor are the only ones a group visitor sees
//   in a company where they are not a member (see features/company-select/group-visitor.ts).
// - The highlighted item is the FIRST one whose url, or one of its alsoActiveFor
//   prefixes, starts the current path, so a cabinet url must not start a module url.
// `tasksCount` is the number the notification bell shows; «Užduotys» repeats it as its badge.
// The cabinet summary adds two hints: «Kuro ataskaitos» is hidden once the summary says the
// person has no use for it (shown while that is not known), and «Komandiruotės» gets a dot
// when a trip report waits. The assets module's badge is the summary's count of asset requests.
export const useMainMenuItems = (companyId, { tasksCount = 0 } = {}) => {
  const { t } = useTranslation();
  const recognitionUnread = useSelector(
    (s) => s.recognitionReducer?.unreadByCompany?.[companyId] || 0
  );
  const recognitionUnreadMine = useSelector(
    (s) => s.recognitionReducer?.unreadMineByCompany?.[companyId] || 0
  );
  const role = useSelector((s) => s.authReducer?.role);
  const userId = useSelector((s) => s.authReducer?.session?.id);
  const summary = useSelector((s) => selectCabinetSummary(s, companyId));
  const hasFuel = summary?.capabilities?.hasFuel;
  const tripReportWaits = (summary?.personal?.tripReport?.count ?? 0) > 0;
  const fuelReportWaits = (summary?.personal?.fuelReport?.count ?? 0) > 0;
  const assetRequestsPending = summary?.queues?.assetRequests?.count ?? 0;

  const profileUrl = `/app/${companyId}/profile/${userId}`;

  // Literal types, so the layout's type of `group` rejects a misspelled group.
  const CABINET = /** @type {const} */ ("cabinet");
  const COMPANY = /** @type {const} */ ("company");
  const MODULES = /** @type {const} */ ("modules");

  const MainMenuItems = [
    // --- Mano kabinetas: the person's own work, by module ---
    {
      id: "cab-tasks",
      group: CABINET,
      url: `/app/${companyId}/my-work`,
      alsoActiveFor: [`/app/${companyId}/unsigned-files`],
      label: t("tasks"),
      icon: ClipboardCheckIcon,
      badge: tasksCount,
      notForGuestMember: true,
    },
    // The rest of the cabinet lives under the person's own profile address.
    ...(userId
      ? [
          {
            id: "cab-leaves",
            group: CABINET,
            url: `${profileUrl}/leaves`,
            label: t("profile_leaves_tab"),
            icon: CalendarTimeIcon,
            notForGuestMember: true,
          },
          ...(hasFuel === false
            ? []
            : [
                {
                  id: "cab-fuel",
                  group: CABINET,
                  url: `${profileUrl}/fuel-reports`,
                  alsoActiveFor: [`${profileUrl}/expenses`],
                  label: t("fuel_reports"),
                  icon: DeliveryIcon,
                  needsAttention: fuelReportWaits,
                  notForGuestMember: true,
                },
              ]),
          {
            id: "cab-trips",
            group: CABINET,
            url: `${profileUrl}/business-trips`,
            label: t("business_trips"),
            icon: AirplaneIcon,
            needsAttention: tripReportWaits,
            notForGuestMember: true,
          },
          {
            id: "cab-assets",
            group: CABINET,
            url: `${profileUrl}/assets`,
            label: t("cabinet_assets"),
            icon: InventoryIcon,
            notForGuestMember: true,
          },
          {
            id: "cab-docs",
            group: CABINET,
            url: `${profileUrl}/files`,
            alsoActiveFor: [
              `${profileUrl}/onboarding-documents`,
              `${profileUrl}/downloads`,
              // A document opened from the tree has no user in its address.
              `/app/${companyId}/profile/files`,
            ],
            // "Mano": the company's documents module is a separate item lower down.
            label: t("cabinet_my_documents"),
            icon: FileIcon,
            notForGuestMember: true,
          },
          {
            id: "cab-safety",
            group: CABINET,
            url: `${profileUrl}/roles`,
            alsoActiveFor: [`${profileUrl}/medical-check`],
            label: t("cabinet_safety"),
            icon: ShieldCheckMarkIcon,
            notForGuestMember: true,
          },
          {
            // Last, so every other profile page is claimed by its own item first; this one
            // also owns the bare profile address.
            id: "cab-about",
            group: CABINET,
            url: `${profileUrl}/info`,
            alsoActiveFor: [profileUrl],
            label: t("cabinet_about_me"),
            icon: ProfileIcon,
          },
        ]
      : []),
    // --- Įmonė: what is shared in the company ---
    {
      id: 12,
      group: COMPANY,
      url: `/app/${companyId}/posts`,
      label: t("news"),
      icon: MegaphoneIcon,
      isVisibleToGroupVisitor: true,
    },
    {
      id: 12.6,
      group: COMPANY,
      url: `/app/${companyId}/leave-calendar`,
      label: t("team_calendar"),
      icon: CalendarIcon,
      notForGuestMember: true,
    },
    {
      id: 12.7,
      group: COMPANY,
      url: `/app/${companyId}/recognition`,
      label: t("recognition", { defaultValue: "Recognition" }),
      icon: ThumbsUpIcon,
      badge: recognitionUnread,
      hasPersonal: recognitionUnreadMine > 0,
      notForGuestMember: true,
    },
    {
      id: 10,
      group: COMPANY,
      url: `/app/${companyId}/about-company`,
      label: t("about_company"),
      icon: InfoIcon,
      isVisibleToGroupVisitor: true,
    },
    // --- Moduliai: the company's work areas ---
    {
      id: 2,
      group: MODULES,
      url: `/app/${companyId}/customers`,
      label: t("customers"),
      icon: OrganizationIcon,
      isVisibleOnlyFor: [
        "Master",
        "Admin",
        "SalesMan",
        "Signature",
        "Access all customers",
      ],
    },
    {
      id: 3,
      group: MODULES,
      url: `/app/${companyId}/products`,
      label: t("products"),
      icon: ProductIcon,
      isVisibleOnlyFor: ["Master", "Admin", "SalesMan", "Signature"],
    },
    {
      id: 3.1,
      group: MODULES,
      url: `/app/${companyId}/product-groups`,
      label: t("product_groups"),
      icon: ProductReferenceIcon,
      isVisibleOnlyFor: ["Master", "Admin", "SalesMan", "Signature"],
    },
    {
      id: 4,
      group: MODULES,
      url: `/app/${companyId}/reservations`,
      label: t("reservations"),
      icon: ProductReferenceIcon,
      isVisibleOnlyFor: ["Master", "Admin", "SalesMan", "Signature"],
    },
    {
      id: 5,
      group: MODULES,
      url: `/app/${companyId}/project-boards`,
      label: t("projects"),
      icon: PageAttachmentIcon,
      isVisibleOnlyFor: ["Master", "Access project"],
    },
    {
      id: 6,
      group: MODULES,
      url: `/app/${companyId}/documents`,
      label: t("module_documents"),
      icon: PageAttachmentIcon,
      isVisibleOnlyFor: ["Master", "Admin", "Access document"],
    },
    {
      id: 7,
      group: MODULES,
      url: `/app/${companyId}/assets`,
      label: t("assets"),
      icon: InventoryIcon,
      isVisibleOnlyFor: ["Master", "Access assets"],
      badge: assetRequestsPending,
    },
    {
      id: 8,
      group: MODULES,
      url: `/app/${companyId}/fuel-reports`,
      label: t("module_fuel_reports"),
      icon: DeliveryIcon,
      isVisibleOnlyFor: ["Master", "Access fuel reports"],
    },
    {
      id: 8.5,
      group: MODULES,
      url: `/app/${companyId}/business-trips`,
      label: t("module_business_trips"),
      icon: AirplaneIcon,
      isVisibleOnlyFor: ["Master", "Access business trips"],
    },
    {
      id: 8.6,
      group: MODULES,
      // Opens the first tab the viewer may see; «Darbo sutartys», «Pareigos» and «Skyriai»
      // are Personalas tabs with their own paths.
      url: personalasHomePath(companyId, role),
      label: t("hr_module", { defaultValue: "HR" }),
      icon: PersonIcon,
      alsoActiveFor: [
        `/app/${companyId}/onboarding`,
        `/app/${companyId}/positions`,
        `/app/${companyId}/departments`,
      ],
      isVisibleOnlyFor: PERSONALAS_MENU_GROUPS,
    },
    {
      id: 8.65,
      group: MODULES,
      url: `/app/${companyId}/work-security`,
      label: t("work_security_module_title", { defaultValue: "Company internal documents" }),
      icon: PersonLockIcon,
      isVisibleOnlyFor: [
        "Master",
        "Access work security assignments",
        "Access work security module",
      ],
    },
    {
      id: 8.66,
      group: MODULES,
      url: `/app/${companyId}/desk/today`,
      label: t("desk", { defaultValue: "Desk" }),
      icon: EmailIcon,
      isVisibleOnlyFor: ["Master", "Access desk"],
    },
    {
      id: 8.68,
      group: MODULES,
      url: `/app/${companyId}/invoice-inbox`,
      label: t("invoice_inbox", { defaultValue: "Incoming invoices" }),
      icon: ReceiptIcon,
      isVisibleOnlyFor: ["Master", "Access invoice inbox"],
    },
    {
      id: 8.69,
      group: MODULES,
      url: `/app/${companyId}/outgoing-invoices`,
      label: t("outgoing_invoices", { defaultValue: "Outgoing invoices" }),
      icon: ListNumberedIcon,
      isVisibleOnlyFor: [
        "Master",
        "Admin",
        "Signature",
        "Access all customers",
      ],
    },
    {
      id: 9,
      group: MODULES,
      url: `/app/${companyId}/orders`,
      label: t("orders"),
      icon: OrderIcon,
      isVisibleOnlyFor: [
        "Master",
        "Admin",
        "Signature",
        "Access all customers",
      ],
    },
    {
      id: 11,
      group: MODULES,
      // One entry: every settings page sits in the settings layout, whose own
      // navigation lists them. The pages that keep their own URL still highlight it.
      url: `/app/${companyId}/settings`,
      label: t("settings"),
      icon: SettingsIcon,
      alsoActiveFor: settingsAreaPaths().map((path) => `/app/${companyId}/${path}`),
      isVisibleOnlyFor: ["Master"],
    },
  ];

  return { MainMenuItems };
};
